import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import styled from 'styled-components';

import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import TextWithVariables from '../../../../../components/shared/TextWithVariables';
import ChatbotOptionModel from '../../../../../components/chatbots/ChatbotOptionModel';
import showtoast from '../../../../../util/toast';
import DeactivationModal from '../../../OutboundFollowUps/DeactivationModal';
import OutboundFollowUpWaitingTimes from '../../../OutboundFollowUps/OutboundFollowUpWaitingTimes';
import {
  getOutboundFollowUpConfig,
  updateOutboundFollowUpConfig,
  resetSaved
} from '../../../../../actions/outboundFollowUps';

const Label = styled.span`
  font-family: 'Poppins-SemiBold';
  font-weight: 600;
  font-size: 14px;
  color: #23292E;
`;

const Description = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: #23292E;
`;

const MAX_HOURS_TOTAL = 24;

const totalHours = (waitingTimes) => (waitingTimes || [])
  .filter((wt) => !wt._destroy)
  .reduce((sum, wt) => {
    const val = Number(wt.waiting_time) || 0;
    return sum + (wt.waiting_time_type === 'hours' ? val : val / 60);
  }, 0);

const buildPayload = (formData) => ({
  outbound_follow_up_config: {
    enabled: formData.enabled,
    message_body: formData.message_body,
    auto_resolve: formData.auto_resolve,
    outbound_follow_up_waiting_times_attributes: (formData.waiting_times || []).map((wt, index) => ({
      ...(wt.id ? { id: wt.id } : {}),
      waiting_time: wt.waiting_time,
      waiting_time_type: wt.waiting_time_type,
      position: index,
      _destroy: wt._destroy || false,
    })),
  },
});

const OutboundFollowUpsModal = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { config, loading, saving, saved, error } = useSelector((state) => state.outboundFollowUpsReducer);

  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);
  const [customerColumns, setCustomerColumns] = useState([]);

  useEffect(() => {
    dispatch(getOutboundFollowUpConfig());
    ChatbotOptionModel.getCustomerColumns().then((res) => {
      setCustomerColumns(res.fields || []);
    });
  }, [dispatch]);

  const loadingRef = useRef(false);

  useEffect(() => {
    if (loading) {
      loadingRef.current = true;
    }

    if (loadingRef.current && !loading && !formData) {
      setFormData({
        enabled: config?.enabled || false,
        message_body: config?.message_body || '',
        auto_resolve: config?.auto_resolve || false,
        waiting_times: config?.waiting_times || [],
      });
    }
  }, [config, formData, loading]);

  useEffect(() => {
    if (saved) {
      showtoast(t('outbound_follow_ups.saveSuccess'));
      dispatch(resetSaved());
      dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
    }
  }, [saved, dispatch, t]);

  useEffect(() => {
    if (error) {
      showtoast(error);
    }
  }, [error]);

  const validate = (_formData) => {
    const _errors = {};
    if (_formData.enabled) {
      if (!_formData.message_body?.trim()) {
        _errors.message_body = t('outbound_follow_ups.messageRequired');
      }
      const active = (_formData.waiting_times || []).filter((wt) => !wt._destroy);
      if (active.length === 0) {
        _errors.waiting_times = t('outbound_follow_ups.waitingTimesRequired');
      } else if (totalHours(_formData.waiting_times) > MAX_HOURS_TOTAL) {
        _errors.waiting_times = t('outbound_follow_ups.totalTimeLimit');
      }
    }
    setErrors(_errors);
    return isEmpty(_errors);
  };

  const handleToggleEnabled = (newValue) => {
    if (!newValue && config?.enabled) {
      setShowDeactivationModal(true);
    } else {
      setFormData((prev) => ({ ...prev, enabled: newValue }));
    }
  };

  const handleConfirmDeactivation = () => {
    setShowDeactivationModal(false);
    setFormData((prev) => ({ ...prev, enabled: false }));
  };

  const handleSave = useCallback(() => {
    if (!validate(formData)) return;
    dispatch(updateOutboundFollowUpConfig(buildPayload(formData)));
  }, [formData, dispatch]);

  const handleClose = useCallback(() => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  }, [dispatch]);

  if (loading || !formData) {
    return (
      <RightModalLayout title={t('outbound_follow_ups.title')} closeAction={handleClose}>
        <div className="tw-flex tw-justify-center tw-items-center tw-h-64">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">{t('outbound_follow_ups.loading')}</span>
          </div>
        </div>
      </RightModalLayout>
    );
  }

  return (
    <>
      <RightModalLayout
        title={t('outbound_follow_ups.title')}
        closeAction={handleClose}
        submitLabel={saving ? t('outbound_follow_ups.saving') : t('outbound_follow_ups.save')}
        cancelLabel={t('outbound_follow_ups.cancel')}
        submitAction={handleSave}
        cancelAction={handleClose}
        submitDisabled={saving}
      >
        <div>
          <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
            <label className="switch tw-mb-0 tw-flex-shrink-0" style={{ top: 0 }}>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleToggleEnabled(e.target.checked)}
              />
              <span className="slider round" />
            </label>
            <Label>{t('outbound_follow_ups.enableLabel')}</Label>
          </div>

          <Description className="tw-mb-4">{t('outbound_follow_ups.description')}</Description>


          {formData.enabled && (
            <>
              <div className="row">
                <TextWithVariables
                  label={t('outbound_follow_ups.messageLabel')}
                  field="message_body"
                  classNameTextArea="input-mercately-border mt-8"
                  updateValue={(_field, value) => setFormData((prev) => ({ ...prev, message_body: value }))}
                  errors={errors}
                  customerColumns={customerColumns}
                  value={formData.message_body}
                />
              </div>

              <div className="row">
                <OutboundFollowUpWaitingTimes
                  waitingTimes={formData.waiting_times}
                  onChange={(wts) => setFormData((prev) => ({ ...prev, waiting_times: wts }))}
                  errors={errors.waiting_times}
                />
              </div>

              {(() => {
                const hours = totalHours(formData.waiting_times);
                const h = Math.floor(hours);
                const m = Math.round((hours - h) * 60);
                const isOver = hours > MAX_HOURS_TOTAL;
                return (
                  <div
                    role="status"
                    className="tw-text-xs tw-mt-2 tw-mb-2"
                    style={{ color: isOver ? '#C0392B' : '#45535F' }}
                  >
                    {t('outbound_follow_ups.totalTime', { hours: h, minutes: m, max: MAX_HOURS_TOTAL })}
                  </div>
                );
              })()}

              <div className="tw-py-4 tw-mt-4" style={{ borderTop: '1px solid #E9EDF0' }}>
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                  <label className="switch tw-mb-0 tw-flex-shrink-0" style={{ top: 0 }}>
                    <input
                      type="checkbox"
                      checked={formData.auto_resolve}
                      onChange={(e) => setFormData((prev) => ({ ...prev, auto_resolve: e.target.checked }))}
                    />
                    <span className="slider round" />
                  </label>
                  <Label>{t('outbound_follow_ups.autoResolveLabel')}</Label>
                </div>
                <Description>{t('outbound_follow_ups.autoResolveDescription')}</Description>
              </div>
            </>
          )}
        </div>
      </RightModalLayout>

      <DeactivationModal
        visible={showDeactivationModal}
        onConfirm={handleConfirmDeactivation}
        onCancel={() => setShowDeactivationModal(false)}
      />
    </>
  );
};

export default OutboundFollowUpsModal;
